import {Lang} from 'yougile-lang'
import {FilenameUtil, logger, Time, Util, UUID} from 'yougile-util'
import {
  ChatMessage,
  DataObjectId,
  groupChatContainerId,
  howToNotifyOnMessage,
  Hub,
  isUserMentionedInMessage,
  rootNodeId,
  SandboxTree,
  SystemMessageUtils,
  UserAccount,
  youGileUserId
} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {DesktopRenderer} from '@client/desktop-renderer'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {Sync} from '@client/sync'
import {SyncEvents, SyncUpdate} from '@client/sync-events'
import {Debouncer} from '@common/debouncer'
import {MessageUtils} from '@extensions/message-utils'
import {NotificationSettingsService} from '@extensions/notifications-settings-service'
import {NotyNotify, NotyRing} from '../types/noty'
import {ChatSubscribers} from './chat-subscribers'
import {ChatUtils} from './chat-utils'
import {globals} from './globals-base'
import {getAllowedMirroredOldestHub} from './mirrored-tasks/get-allowed-mirrored-oldest-hub'
import {Sticker} from './sticker/sticker'

const newMessagesWas: Map<string, number> = new Map() // path -> number
const lastNewMessageTimestamp: Map<string, number> = new Map() // path -> timestamp

interface HowToNotify {
  sound: boolean
  push: boolean
  mobilePush: boolean
}
const noNotify: Readonly<HowToNotify> = {
  sound: false,
  push: false,
  mobilePush: false
}

export class MessageNotifier {
  private static newCompanyListenerId: UUID
  private static listenerIds: Map<DataObjectId, number> = new Map()
  private static listenerDndIds: Map<DataObjectId, number> = new Map()
  private static notify: NotyNotify
  private static ring: NotyRing
  private static pendingUpdates: SyncUpdate[] = []

  private static soundDebouncer: Debouncer<number>

  public static markRead(path: string): void {
    newMessagesWas.set(path, 0)
  }

  public static init(notify: NotyNotify, ring: NotyRing): void {
    if (SharedBoardsClient.getId()) {
      return
    }
    MessageNotifier.notify = notify
    MessageNotifier.ring = ring
    MessageNotifier.soundDebouncer = new Debouncer<number>(300)
    MessageNotifier.soundDebouncer.onReady((data: number[]) => {
      const cnt = data.reduce((a: number, b: number) => a + b)
      MessageNotifier.ring(cnt > 1 ? 'chat6' : 'chat5')
    })

    const addListener = (sb: SandboxTree) => {
      if (!sb) {
        return
      }
      SyncEvents.removeListener(MessageNotifier.listenerIds.get(sb.companyId))
      SyncEvents.removeListener(
        MessageNotifier.listenerDndIds.get(sb.companyId)
      )

      MessageNotifier.listenerIds.set(
        sb.companyId,
        SyncEvents.addListener(sb.companyId, '*', (res: SyncUpdate[]) => {
          MessageNotifier.processUpdatedEntities(res)
        })
      )
      MessageNotifier.listenerDndIds.set(
        sb.companyId,
        SyncEvents.addListener(
          sb.companyId,
          () => globals.currentUserId,
          () => {
            const acc = Accounts.get(globals.currentUserId)
            if (MessageNotifier.pendingUpdates && !UserAccount.isInDnd(acc)) {
              MessageNotifier.processUpdatedEntities(
                MessageNotifier.pendingUpdates
              )
              MessageNotifier.pendingUpdates = []
            }
          }
        )
      )
    }

    if (globals.currentUserId) {
      Sync.listSandboxes().forEach(addListener)
      if (!MessageNotifier.newCompanyListenerId) {
        MessageNotifier.newCompanyListenerId = SyncEvents.addNewCompanyListener(
          companyId => {
            const sb = Sync.get({companyId})
            addListener(sb)
          }
        )
      }
    }
  }

  private static processUpdatedEntities(updates: SyncUpdate[]): void {
    const userId = globals.currentUserId
    if (!userId) {
      return
    }
    let notifyBody = ''
    let notifyIcon = ''
    let notifyTitle = ''
    let notifyClickCallback = Util.noop
    let messagesCount = 0
    let notifySound = false
    let notifyPush = false

    const inDnd = UserAccount.isInDnd(Accounts.get(userId))

    updates.forEach(upd => {
      const box = Sync.get(upd)
      upd.ids.forEach(id => {
        if (id.includes('/.') || id === rootNodeId) {
          return
        }

        const entity = box.get(id)
        if (entity instanceof Hub) {
          const oldestHub = getAllowedMirroredOldestHub(entity)
          if (oldestHub && oldestHub !== entity.id) {
            return
          }
        }

        if (
          !entity ||
          entity.deleted ||
          !(entity instanceof Hub) ||
          box.isDeleted(id)
        ) {
          return
        }

        const newMessages = entity.chat.getNewMessages(userId)
        const isSubscribed = ChatSubscribers.isSubscribed(userId, entity)
        const mentioned = newMessages
          .reverse()
          .find(m => isUserMentionedInMessage(userId, m))

        if (!isSubscribed && !mentioned) {
          return
        }

        const prevMessageTimestamp = lastNewMessageTimestamp.get(id) ?? 0
        const lastMessage = isSubscribed ? entity.chat.lastMessage() : mentioned

        if (!lastMessage) {
          return
        }

        if (
          lastMessage &&
          newMessages.length > (newMessagesWas.get(id) ?? 0) &&
          prevMessageTimestamp < lastMessage.timestamp
        ) {
          logger.log(
            'New messages',
            newMessages.length,
            newMessagesWas.get(id) || 0
          )

          if (
            lastMessage.from &&
            box.companyId === Sync.profileId &&
            !box.isUserInCompany(lastMessage.from)
          ) {
            box.restoreUserInProfile(lastMessage.from)
          }

          if (lastMessage.timestamp > Time.now() - 3600 * 1000) {
            const notifyRes = SharedBoardsClient.getId()
              ? noNotify
              : howToNotifyOnMessage(
                  NotificationSettingsService.getNotificationSettings(),
                  globals.currentUserId,
                  entity.toJSON(),
                  lastMessage.toJSON(),
                  Sticker.getUsers(entity)
                )

            if (!Object.values(notifyRes).some(notifyKey => !!notifyKey)) {
              return
            }
            notifySound ||= notifyRes.sound
            notifyPush ||= notifyRes.push

            messagesCount++
            const fromName = Accounts.getName(lastMessage.from, entity)
            if (
              upd.companyId === Sync.profileId ||
              entity.location === groupChatContainerId
            ) {
              notifyTitle = Lang.msg.userWrote(fromName)
              notifyIcon = Accounts.get(lastMessage.from).avatar
              notifyBody = MessageNotifier.decorateMessageBody(lastMessage)
            } else {
              notifyTitle = entity.title
              notifyIcon = Accounts.get(lastMessage.from).avatar
              const prefix =
                lastMessage.from && lastMessage.from !== youGileUserId
                  ? fromName + ': '
                  : ''
              notifyBody =
                prefix + MessageNotifier.decorateMessageBody(lastMessage)
            }

            notifyClickCallback = () => {
              if (Util.isDesktop()) {
                DesktopRenderer.showApp()
              }
              ChatUtils.openChat(entity)
            }
          }

          lastNewMessageTimestamp.set(id, lastMessage.timestamp)
        }

        if (!inDnd) {
          newMessagesWas.set(id, newMessages.length)
        } else {
          MessageNotifier.pendingUpdates.push(upd)
        }
      })
    })

    if (messagesCount > 0 && !inDnd) {
      notifyPush &&
        MessageNotifier.notify(
          notifyTitle,
          notifyIcon,
          notifyBody,
          notifyClickCallback
        )
      notifySound && MessageNotifier.soundDebouncer.process(messagesCount)
    }
  }

  private static decorateMessageBody(msg: ChatMessage): string {
    const text = SystemMessageUtils.isSystemMessage(msg)
      ? MessageUtils.systemMessageText(msg)
      : msg.text

    return text.replace(
      FilenameUtil.rx,
      (m: string) =>
        Lang.msg.fileNoun.one +
        ': ' +
        Util.safeDecodeUriComponent(
          Util.safeDecodeUriComponent(m.split('/').pop() || '', ''),
          ''
        )
          .split('?')
          .shift()
    )
  }
}
